import Checkbox from '../Checkbox';
import React, { useEffect, useState, forwardRef, useCallback, useImperativeHandle } from 'react';
import Popper from '@mui/material/Popper';
import { ClickAwayListener } from '@mui/base';
import Box from '@mui/material/Box';
import { textCss } from '../Css';
import IconToolTip from '../IconToolTip';
import { SkeltonCardTwo } from '../SkeltonCardTwo';
import { useSelector } from 'react-redux';

export const CheckBoxFilter = forwardRef((props, ref) => {
    const { filterName, title, filterList, setIsApplyDisabled, isSingleDropDown, showSelectedFilters, localFilters, setLocalFilters, keysToFilter, onSearch, filter_label } = props;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [localList, setLocalList] = useState([]);
    const [searchList, setSearchList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    useImperativeHandle(ref, () => ({
        getLocalFilter() {
            return { [filterName]: localList };
        },
    }));

    const curentFilter = localFilters?.[filterName]

    const handleSearch = useCallback((term, baseList = localList) => {
        const search = term.toLowerCase();
        setSearchTerm(search);
        if (search === '') {
            // When search is cleared, show all items
            setSearchList(baseList);
        } else {
            const filtered = baseList.filter((filx) => filx?.displayValue?.toLowerCase().includes(search));
            setSearchList(filtered);
        }
    }, [localList]);


    // Clear search function to ensure proper reset
    const handleClearSearch = useCallback(() => {
        setSearchTerm('');
        setSearchList(localList);
    }, [localList]);

    useEffect(() => {
        if (Array.isArray(curentFilter) && curentFilter?.length !== localList?.length) {
            setLocalList(prevState => {
                let defaultIsSelected = true;
                if (prevState.find((pre) => !pre.isSelected)) {
                    defaultIsSelected = false;
                }
                const updatedList = curentFilter.map(item => {
                    const match = prevState.find(p => p.apiValue === item.apiValue);
                    if (match) {
                        defaultIsSelected = match.isSelected;
                        return {
                            ...item,
                            isSelected: defaultIsSelected
                        };
                    }

                    if (!match && prevState.length > 0) {
                        return {
                            ...item,
                            isSelected: false
                        }
                    }
                    return item;
                });
                return updatedList;
            });

            if (searchTerm) {
                handleSearch(searchTerm, curentFilter);
            } else {
                handleClearSearch();
            }
        }
    }, [curentFilter, filterName, localList?.length, searchTerm, handleSearch, handleClearSearch]);



    useEffect(() => {
        // Whenever filters changed(select/unselect), checking and filtering out local filters using sieve(filter method) defined in filterSchema
        let updatedFilterList = {};
        keysToFilter.forEach((filter) => {
            if (filter.sieveForFilterItems && typeof filter.sieveForFilterItems === 'function' && localList.length > 0) {
                const currentFilter = {};
                const values = filter.sieveForFilterItems({ filterList, currentFilter: { [filterName]: localList } });
                if (values) currentFilter[filter.filterName] = values;
                updatedFilterList = { ...updatedFilterList, ...currentFilter };
            }
        });
        setLocalFilters((prevState) => {
            return { ...prevState, ...updatedFilterList }
        });
    }, [localList, keysToFilter, setLocalFilters, filterList, filterName]);

    useEffect(() => {
        let selectedValue = localList.filter((filx) => filx.isSelected);
        let isFilterNotSelected = selectedValue.length === 0;
        setIsApplyDisabled(isFilterNotSelected);
    }, [localList, setIsApplyDisabled]);

    const inputRef = useCallback((inputElement) => {
        if (inputElement) {
            inputElement.focus();
        }
    }, []);

    const handleSelect = ({ checked, index }) => {
        let currentFilter = JSON.parse(JSON.stringify(localList));
        let findFilter = currentFilter.find((fil) => fil.index === index);
        if (findFilter) {
            currentFilter.find((fil) => fil.index === index).isSelected = checked;
        }
        setLocalList(currentFilter);
    };

    const handleSelectAll = ({ checked }) => {
        let previousFilter = JSON.parse(JSON.stringify(localList));

        if (searchTerm && searchList.length > 0) {
            // If there's a search term, only update the filtered items
            const updatedFilter = previousFilter.map((filter) => {
                const isInSearchResults = searchList.some(searchItem => searchItem.index === filter.index);
                if (isInSearchResults) {
                    return { ...filter, isSelected: checked };
                }
                return filter;
            });
            setLocalList(updatedFilter);
        } else {
            // If no search term, select/deselect all items
            const currentFilter = previousFilter.map((filter) => {
                return { ...filter, isSelected: checked };
            });
            setLocalList(currentFilter);
        }
    };


    const handleChange = (e) => {
        const keyword = e.target.value.trim();
        if (typeof onSearch === 'function') onSearch(keyword.toLowerCase(), filter_label);

        // If the search input is empty, clear the search properly
        if (keyword === '') {
            handleClearSearch();
        } else {
            handleSearch(keyword);
        }
    };




    // Update searchList when localList changes to reflect selection changes
    useEffect(() => {
        if (searchTerm) {
            handleSearch(searchTerm, localList);
        } else {
            handleClearSearch();
        }
    }, [localList, searchTerm, handleSearch, handleClearSearch]);

    const handleClick = (event) => {
        if (anchorEl) {
            // When closing the dropdown, reset search if needed
            if (searchTerm) {
                handleClearSearch();
            }
        }
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;

    const boxStyle = {
        border: 0,
        p: 1,
        maxHeight: '18rem',
        width: '28rem',
        bgcolor: 'white',
    };

    const onClickAwayHandler = () => {
        if (!anchorEl) return;
        if (anchorEl) setAnchorEl(false);
        setSearchList(curentFilter);
    };

    const selectedCheckboxTexts = () => {
        let filters = localList;
        if (Array.isArray(filters) && filters.length > 0) {
            filters = filters.filter((filx) => filx.isSelected).slice(0, 8);
            return filters.map((filter) => filter.displayValue + ', ');
        }
        return 'Select';
    };

    const GetSelectedCheckbox = () => {
        return (
            <div className='flex flex-col gap-2'>
                {Array.isArray(localList) &&
                    localList.map((filter, index) => {
                        if (filter.isSelected) {
                            return (
                                <div className='flex items-center gap-2'>
                                    <IconToolTip title='Remove'>
                                        <span
                                            onClick={() =>
                                                handleSelect({
                                                    checked: false,
                                                    index,
                                                    filterName,
                                                })
                                            }
                                            className='material-icons text-scogoprimary text-font16 cursor-pointer'
                                        >
                                            highlight_off
                                        </span>
                                    </IconToolTip>
                                    <p className={`${textCss.inputText}`}>{filter.displayValue}</p>
                                </div>
                            );
                        }
                        return <></>;
                    })}
            </div>
        );
    };

    return (
        <div>
            <h1 className={`${textCss?.headerTwo} px-3 py-2`}>{title}</h1>
            <div className='flex mx-2 bg-white border-2 rounded-md'>
                <ClickAwayListener onClickAway={onClickAwayHandler}>
                    <div className='w-full rounded-b-xl cursor-pointer'>
                        <div className={`${textCss?.headerThree} w-full px-2 pr-6 my-2 truncate h-7  relative`} onClick={handleClick}>
                            {selectedCheckboxTexts()}
                            <span className={'material-icons absolute right-0 cursor-pointer '}>arrow_drop_down</span>
                        </div>

                        {isSingleDropDown ?
                            <SingleCheckBoxDropdown filter_label={filter_label} handleChange={handleChange} inputRef={inputRef} searchList={searchList} localList={localList} filterName={filterName} handleSelectAll={handleSelectAll} handleSelect={handleSelect} />
                            :
                            <Popper id={id} open={open} anchorEl={anchorEl} className='z-50 absolute pt-3'>
                                <Box sx={boxStyle} className='shadow-2xl overflow-auto'>
                                    <input
                                        onChange={handleChange}
                                        className={`${textCss?.headerThree}  w-full py-1 my-2 px-2 border-2 rounded-l`}
                                        placeholder={'Search'}
                                        ref={inputRef}
                                        autoFocus
                                    />
                                    <CheckboxList filter_label={filter_label} searchList={searchList} localList={localList} filterName={filterName} handleSelectAll={handleSelectAll} handleSelect={handleSelect} />
                                </Box>
                            </Popper>
                        }

                    </div>
                </ClickAwayListener>
            </div>

            <div className='mt-2 px-3 overflow-auto max-h-28'>{showSelectedFilters && localList?.find((filx) => !filx.isSelected) && <GetSelectedCheckbox />}</div>
        </div>
    );
});



const SingleCheckBoxDropdown = ({ handleChange, inputRef, searchList, localList, filterName, handleSelectAll, handleSelect, filter_label }) => {
    return <div  >
        <input
            onChange={handleChange}
            className={`${textCss?.headerThree}  w-full py-1 my-2 px-2 border-2 rounded-l`}
            placeholder={'Search'}
            ref={inputRef}
            autoFocus
        />
        <div className='max-h-48 overflow-auto'  >
            <CheckboxList filter_label={filter_label} searchList={searchList} localList={localList} filterName={filterName} handleSelectAll={handleSelectAll} handleSelect={handleSelect} />
        </div>
    </div>

}



const CheckboxList = ({ searchList, localList, filterName, handleSelectAll, handleSelect, filter_label }) => {
    const isSelectedFilter = (index, filterName) => {
        return localList.find((fil) => fil.index === index).isSelected;
    };

    // Check if all visible items are selected (search results if searching, otherwise all items)
    const isAllSelected = searchList?.length > 0
        ? searchList.every((filter) => isSelectedFilter(filter.index, filterName))
        : localList?.every((filter) => filter.isSelected);

    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    return (
        <div className='flex flex-col gap-2 px-2'>
            {formButtonLoading[`filter_${filter_label}`] ? <SkeltonCardTwo arr={[1, 2, 3]} height={10} /> : <>
                {
                    <Checkbox
                        CheckContent={'Select All'}
                        checkColor='text-scogoorange mr-2'
                        onChange={(checked) => handleSelectAll({ checked, filterName })}
                        textStyle={`${textCss?.inputText}`}
                        dynamicSize={'1.3rem'}
                        checked={isAllSelected}
                    />
                }
                {searchList?.map((filter, index) => (
                    <Checkbox
                        CheckContent={filter.displayValue}
                        checkColor='text-scogoorange mr-2'
                        onChange={(checked) =>
                            handleSelect({
                                checked,
                                index: filter.index,
                                filterName,
                            })
                        }
                        key={index}
                        textStyle={`${textCss?.inputText}`}
                        dynamicSize={'1.3rem'}
                        checked={isSelectedFilter(filter.index, filterName)}
                    />
                ))}
            </>}
        </div>
    );
}